// Control geometry → hexes. Runs once per control episode at world creation, never per frame.
//
//   admin1 codes  every hex whose admin1 field is that unit (CSR index over the grid)
//   points        hexes whose centre lies within radius_km (always at least the hex holding the point)
//   GeoJSON       7 sample points per candidate hex (centre + 6 at half radius) tested against the
//                 polygons; share of samples inside = fraction. A polygon too small to hold a
//                 sample falls back to its centroid hex, fraction = polygon area / hex area.

import { COLS, HEX_COUNT, HEX_W, ROWS, ROW_H, hexAtLonLat, hexCenter, hexCol, hexIndex, hexLonLat, hexRow,
  lonLatToWorld, neighbour, worldToLonLat, wrapCol } from '../core/grid.ts';
import type { Admin1 } from '../core/schema.ts';
import type { GeoGeometry, GeoJson } from '../data/types.ts';

/** Hexes covered by a geometry, with the covered share of each (0..255). */
export interface HexArea {
  hexes: Int32Array;
  frac: Uint8Array;
}

export const FULL = 255;

/** IUGG mean Earth radius. */
const EARTH_RADIUS_KM = 6371.0088;
const DEG = Math.PI / 180;
const KM_PER_DEG = EARTH_RADIUS_KM * DEG;
const DIRS = 6;
const HALF_CIRCLE_DEG = 180;

/** Sample offsets inside a hex, world units (hex radius = 1). */
const SAMPLE_R = 0.5;
const SAMPLES: ReadonlyArray<readonly [number, number]> = [
  [0, 0],
  ...Array.from({ length: DIRS }, (_, i) => [SAMPLE_R * Math.cos(i * Math.PI / 3), SAMPLE_R * Math.sin(i * Math.PI / 3)] as const),
];

function emptyArea(): HexArea {
  return { hexes: new Int32Array(0), frac: new Uint8Array(0) };
}

function toArea(hexes: number[], frac: number[]): HexArea {
  return { hexes: Int32Array.from(hexes), frac: Uint8Array.from(frac) };
}

/** Share of a hex covered by `km2`, as 1..255 (never 0: the geometry is there). */
function areaFrac(km2: number, hexKm2: number): number {
  if (!(hexKm2 > 0)) {
    return FULL;
  }
  return Math.max(1, Math.min(FULL, Math.round(FULL * km2 / hexKm2)));
}

// ─── admin-1 ─────────────────────────────────────────────────────────────────────────────

export interface Admin1Index {
  /** hexes of admin1 id k are hexes[start[k] .. start[k + 1]). */
  start: Int32Array;
  hexes: Int32Array;
  byCode: Map<string, number>;
}

export function admin1Index(grid: Uint16Array, units: Admin1[]): Admin1Index {
  const maxId = units.reduce((m, u) => Math.max(m, u.id), 0);
  const start = new Int32Array(maxId + 2);
  for (let i = 0; i < HEX_COUNT; i++) {
    const id = grid[i];
    if (id !== 0 && id <= maxId) {
      start[id + 1]++;
    }
  }
  for (let k = 1; k < start.length; k++) {
    start[k] += start[k - 1];
  }

  const hexes = new Int32Array(start[start.length - 1]);
  const fill = start.slice();
  for (let i = 0; i < HEX_COUNT; i++) {
    const id = grid[i];
    if (id !== 0 && id <= maxId) {
      hexes[fill[id]++] = i;
    }
  }
  return { start, hexes, byCode: new Map(units.map((u) => [u.code, u.id])) };
}

/** Hexes of one admin-1 unit by Natural Earth adm1_code; empty when unknown. */
export function admin1Hexes(ix: Admin1Index, code: string): Int32Array {
  const id = ix.byCode.get(code);
  if (id === undefined || id + 1 >= ix.start.length) {
    return new Int32Array(0);
  }
  return ix.hexes.subarray(ix.start[id], ix.start[id + 1]);
}

// ─── points ──────────────────────────────────────────────────────────────────────────────

/** Great-circle distance in km. */
export function distanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = (lat2 - lat1) * DEG;
  const dLon = (lon2 - lon1) * DEG;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * DEG) * Math.cos(lat2 * DEG) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

function centreKm(hex: number, lat: number, lon: number): number {
  const c = hexLonLat(hexCol(hex), hexRow(hex));
  return distanceKm(lat, lon, c.lat, c.lon);
}

/** Flood from the hex holding the point through hexes whose centre is within the radius. */
function discArea(p: { lat: number; lon: number; radius_km: number }, areaKm2: Float32Array, out: Map<number, number>): void {
  const start = hexAtLonLat(p.lon, p.lat);
  const seen = new Set<number>([start]);
  const queue = [start];
  while (queue.length > 0) {
    const h = queue.pop() as number;
    const inside = centreKm(h, p.lat, p.lon) <= p.radius_km;
    if (!inside && h !== start) {
      continue;
    }

    const f = inside ? FULL : areaFrac(Math.PI * p.radius_km ** 2, areaKm2[h]);
    out.set(h, Math.max(out.get(h) ?? 0, f));
    for (let d = 0; d < DIRS; d++) {
      const n = neighbour(h, d);
      if (n >= 0 && !seen.has(n)) {
        seen.add(n);
        queue.push(n);
      }
    }
  }
}

export function pointsArea(points: Array<{ lat: number; lon: number; radius_km: number }>, areaKm2: Float32Array): HexArea {
  const got = new Map<number, number>();
  for (const p of points) {
    discArea(p, areaKm2, got);
  }
  return toArea([...got.keys()], [...got.values()]);
}

// ─── GeoJSON polygons ────────────────────────────────────────────────────────────────────

type Ring = Array<[number, number]>;
type Poly = Ring[];

function geometries(g: GeoJson): GeoGeometry[] {
  if (g.features) {
    return g.features.map((f) => f.geometry).filter((x): x is GeoGeometry => x !== null);
  }
  if (g.geometry) {
    return [g.geometry];
  }
  if (g.type === 'Polygon' || g.type === 'MultiPolygon') {
    return [g as unknown as GeoGeometry];
  }
  return [];
}

function polygonsOf(g: GeoJson): Poly[] {
  const out: Poly[] = [];
  for (const geom of geometries(g)) {
    if (geom.type === 'Polygon') {
      out.push(geom.coordinates);
      continue;
    }
    if (geom.type === 'MultiPolygon') {
      out.push(...geom.coordinates);
    }
  }
  return out;
}

/** Even-odd ray cast over every ring of one polygon (holes subtract). */
function inPoly(poly: Poly, lon: number, lat: number): boolean {
  let inside = false;
  for (const ring of poly) {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi] = ring[i];
      const [xj, yj] = ring[j];
      if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
  }
  return inside;
}

interface Box {
  minLon: number;
  maxLon: number;
  minLat: number;
  maxLat: number;
}

function bbox(polys: Poly[]): Box {
  const b: Box = { minLon: Infinity, maxLon: -Infinity, minLat: Infinity, maxLat: -Infinity };
  for (const poly of polys) {
    for (const [lon, lat] of poly[0] ?? []) {
      b.minLon = Math.min(b.minLon, lon);
      b.maxLon = Math.max(b.maxLon, lon);
      b.minLat = Math.min(b.minLat, lat);
      b.maxLat = Math.max(b.maxLat, lat);
    }
  }
  return b;
}

/** Candidate hex indices covering a lon/lat box, one hex of margin all round. */
function candidates(b: Box): number[] {
  const top = lonLatToWorld(b.minLon, b.maxLat).z;
  const bottom = lonLatToWorld(b.minLon, b.minLat).z;
  const row0 = Math.max(0, Math.floor(top / ROW_H) - 1);
  const row1 = Math.min(ROWS - 1, Math.ceil(bottom / ROW_H) + 1);

  // A box wider than half the world is taken to cross the antimeridian: scan every column.
  const wide = b.maxLon - b.minLon > HALF_CIRCLE_DEG;
  const x0 = lonLatToWorld(b.minLon, 0).x;
  const x1 = lonLatToWorld(b.maxLon, 0).x;
  const col0 = wide ? 0 : Math.floor(x0 / HEX_W) - 1;
  const col1 = wide ? COLS - 1 : Math.ceil(x1 / HEX_W) + 1;

  const out: number[] = [];
  for (let r = row0; r <= row1; r++) {
    for (let c = col0; c <= col1; c++) {
      out.push(hexIndex(wrapCol(c), r));
    }
  }
  return out;
}

function samplesInside(polys: Poly[], hex: number): number {
  const c = hexCenter(hexCol(hex), hexRow(hex));
  let n = 0;
  for (const [dx, dz] of SAMPLES) {
    const ll = worldToLonLat(c.x + dx, c.z + dz);
    if (polys.some((p) => inPoly(p, ll.lon, ll.lat))) {
      n++;
    }
  }
  return n;
}

/** Planar area of a ring in km², projected equirectangularly at its own mean latitude. */
function ringKm2(ring: Ring): number {
  const lat0 = ring.reduce((s, p) => s + p[1], 0) / ring.length;
  const kx = KM_PER_DEG * Math.cos(lat0 * DEG);
  let twice = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    twice += (ring[j][0] * kx) * (ring[i][1] * KM_PER_DEG) - (ring[i][0] * kx) * (ring[j][1] * KM_PER_DEG);
  }
  return Math.abs(twice) / 2;
}

function centroidArea(polys: Poly[], areaKm2: Float32Array): HexArea {
  const outer = polys.map((p) => p[0]).filter((r) => r && r.length > 0);
  if (outer.length === 0) {
    return emptyArea();
  }
  const pts = outer.flat();
  const lon = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lat = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  const hex = hexAtLonLat(lon, lat);
  const km2 = outer.reduce((s, r) => s + ringKm2(r), 0);
  return toArea([hex], [areaFrac(km2, areaKm2[hex])]);
}

export function polygonArea(geo: GeoJson, areaKm2: Float32Array): HexArea {
  const polys = polygonsOf(geo);
  if (polys.length === 0) {
    return emptyArea();
  }

  const hexes: number[] = [];
  const frac: number[] = [];
  for (const h of candidates(bbox(polys))) {
    const n = samplesInside(polys, h);
    if (n === 0) {
      continue;
    }
    hexes.push(h);
    frac.push(Math.round((FULL * n) / SAMPLES.length));
  }
  if (hexes.length === 0) {
    return centroidArea(polys, areaKm2);
  }
  return toArea(hexes, frac);
}
